import { ReactNode } from 'react';

import {
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme
} from '@mui/material';

import { passedSx } from '@/shared/lib/utils';

import { CompactRowHead, TCompactHeadWrap } from './CompactRowHead';
import { buildFieldsRow } from './ListRowFields';
import {
  getCornerSx,
  getDecisionCornerSx,
  getDecisionLaneSx,
  getMediaBoxSx,
  getRootSx,
  getRowSurfaceSx,
  getSubtitleSx,
  IRowField,
  MEDIA_FILL_SX,
  ROW_CONTAINER_SX,
  TIGHT_BODY_SX,
  TITLE_ACTIONS_SX,
  TITLE_ROW_SX,
  TITLE_SLOT_SX,
  TListRowMediaAlign,
  TListRowMediaAspect,
  TListRowTrailingAlign,
  TListRowVariant
} from './listRowFrameStyles';

// Геометрия строки (типы, константы высот, sx-хелперы и их обоснования) живёт
// отдельным модулем `listRowFrameStyles.ts` — файл каркаса упирался в
// лимит длины (350 строк), а разрез «стили отдельно от разметки» не режет ни
// одного ветвления. Публичные имена каркаса ре-экспортируются отсюда, чтобы
// домены продолжали импортировать их из `ListRowFrame`, как раньше.
export {
  buildRowQuery,
  getRowSurfaceSx,
  ROW_CONTAINER,
  WIDE_ROW
} from './listRowFrameStyles';
export type {
  IRowField,
  TListRowMediaAlign,
  TListRowMediaAspect,
  TListRowTrailingAlign,
  TListRowVariant
} from './listRowFrameStyles';

interface IRowMediaProps {
  variant: TListRowVariant;
  aspect: TListRowMediaAspect;
  align: TListRowMediaAlign;
  narrow?: boolean;
  media?: ReactNode;
}

/**
 * Медиа-слот строки. Без переданного `media` не рисуется вовсе — домен без
 * фото/обложки не должен резервировать под него пустое место.
 * @param props - пропсы
 * @param props.variant - `list` или `compact`, определяет высоту слота
 * @param props.aspect - пропорция медиа
 * @param props.align - выключка по кросс-оси строки (`center`/`flex-start`)
 * @param props.narrow - узкое фото 56×75 (см. `mediaNarrow`)
 * @param props.media - содержимое; без него слот отсутствует
 */
const RowMedia = ({
  variant,
  aspect,
  align,
  narrow,
  media
}: IRowMediaProps) => {
  if (!media) {
    return null;
  }

  return (
    <Box sx={getMediaBoxSx(variant, aspect, align, narrow)}>
      <Box sx={MEDIA_FILL_SX}>{media}</Box>
    </Box>
  );
};

interface IRowHeaderProps {
  variant: TListRowVariant;
  title: ReactNode;
  titleActions?: ReactNode;
  meta?: ReactNode;
  compactHeadWrap?: TCompactHeadWrap;
  badges?: ReactNode;
}

/**
 * Заголовочная строка тела — вынесена из `RowBody` отдельным компонентом:
 * три взаимоисключающие ветки (`compact`-голова с `meta`, `list`-заголовок с
 * `titleActions`, голый заголовок без него) разгоняли цикломатическую
 * сложность `RowBody` выше порога линтера. Сам по себе рендер ветвления не
 * меняет — то же дерево, что было инлайновым.
 * @param props - пропсы
 * @param props.variant - `list` или `compact`
 * @param props.title - заголовок строки
 * @param props.titleActions - действия строки заголовка (см. `RowBody`)
 * @param props.meta - готовая строка метаданных `compact`
 * @param props.compactHeadWrap - порог головы `compact` домена (см. `TCompactHeadWrap`)
 * @param props.badges - плашка статуса `compact` (18976, Р56) — флекс-сосед
 * `title`/`meta` в ТОЙ ЖЕ голове, а не отдельный ряд под ней (см. `RowBody`)
 */
const RowHeader = ({
  variant,
  title,
  titleActions,
  meta,
  compactHeadWrap,
  badges
}: IRowHeaderProps) => {
  if (variant === 'compact') {
    return (
      <CompactRowHead
        title={title}
        titleActions={titleActions}
        meta={meta}
        compactHeadWrap={compactHeadWrap}
        badges={badges}
      />
    );
  }

  if (titleActions) {
    return (
      <Box sx={TITLE_ROW_SX}>
        <Box sx={TITLE_SLOT_SX}>{title}</Box>
        <Box sx={TITLE_ACTIONS_SX}>{titleActions}</Box>
      </Box>
    );
  }

  return title;
};

/**
 * Отдельный ряд бейджей под шапкой — ТОЛЬКО `list` (в `compact` плашка живёт в
 * самой голове, см. `RowHeader`, 18976, Р56). Вынесено из `RowBody` именованной
 * функцией: ветвление «есть ли что показывать» разгоняло сложность тела строки
 * выше порога линтера, а рендер оставляет тем же деревом, что был инлайновым.
 * @param badges - статусные чипы/бейджи; без них ряд не рисуется вовсе
 */
const buildListBadgesRow = (badges?: ReactNode): ReactNode => {
  if (!badges) {
    return null;
  }

  return (
    <Stack direction='row' flexWrap='wrap' gap={2}>
      {badges}
    </Stack>
  );
};

interface IRowBodyProps {
  variant: TListRowVariant;
  title: ReactNode;
  titleActions?: ReactNode;
  subtitle?: ReactNode;
  subtitleMuted?: boolean;
  fields?: IRowField[];
  meta?: ReactNode;
  badges?: ReactNode;
  compactHeadWrap?: TCompactHeadWrap;
  decisionSlot?: ReactNode;
  fieldsInline?: boolean;
  tight?: boolean;
}

/**
 * Текстовый блок строки: заголовок (плюс мета в `compact`), бейджи, подзаголовок
 * и — только в `list` — три подписанных поля. Вынесен из `ListRowFrame`, чтобы
 * ветвление по наличию каждого необязательного слота не копилось в одной функции.
 *
 * Корень несёт `data-testid='list-row-body'` — единственный семантический якорь,
 * которым тесты каркаса проверяют «трейлинг вынесен вне текстового блока»
 * (`ListRowFrame.test.tsx`); сам DOM внутри блока тестами не пиновано.
 * @param props - пропсы
 * @param props.variant - `list` (поля) или `compact` (мета рядом с заголовком)
 * @param props.title - заголовок строки
 * @param props.titleActions - действия строки заголовка (сердце[+папка]
 * актёрской строки, 18976, Р3) — флекс-сосед ИМЕННО заголовка (и `meta` в
 * `compact`), а не всей текстовой колонки; резервирует свою ширину там же, где
 * рисуется, вместо абсолютного оверлея поверх строки
 * @param props.subtitle - вторая строка; по умолчанию того же цвета, что и обычное
 * значение поля (контекст `list`, например `additionalInfo`). `ReactNode`, как и
 * значение поля — рендерится `component='div'` по той же причине (см. `FrameField`)
 * @param props.subtitleMuted - вторая строка приглушена (`text.disabled`) — вид
 * `compact` использует это для приписки вырожденного вида (`metaSuffix`),
 * которая объясняет пустое состояние, а не сообщает данные
 * @param props.fields - РОВНО три поля `list`; в `compact` не используется
 * @param props.meta - готовая строка метаданных `compact`
 * @param props.badges - статусные чипы/бейджи
 * @param props.compactHeadWrap - контейнерный запрос, от которого голова `compact`
 * в одну строку (18976, Р8; строкой — фикс-волна 4 19243): уже него `meta`
 * стоит второй строкой — домен ставит ей `flexBasis: '100%'` под тем же
 * порогом (см. `CastingListRow`, `ProjectListRow`), резерв угла — только на
 * первой строке (`CompactRowHead`)
 * @param props.decisionSlot - пара решения («Отклонить»/«Принять», `list`
 * ТОЛЬКО, 18976, Р73, пятый заход) — последний элемент ряда трёх полей, на
 * одной линии с ними на широкой ветке, отдельной строкой под ними на узкой
 * (см. JSDoc `DECISION_SLOT_SX`). `compact` (без ряда полей) держит эту же
 * пару АБСОЛЮТНЫМ нижним углом — `ListRowFrame` рендерит его сам, см.
 * `getDecisionCornerSx`, НЕ передавая сюда: в потоке `RowBody` пара растягивала
 * бы шапку `compact` к верху вместо центрирования (см. JSDoc
 * `getDecisionCornerSx`).
 * @param props.fieldsInline - поля `list` парой «подпись значение» в строку и ниже `WIDE_ROW`
 * @param props.tight - плотный ритм карточки уведомления (`TIGHT_BODY_SX`); по умолчанию выкл
 */
const RowBody = ({
  variant,
  title,
  titleActions,
  subtitle,
  subtitleMuted,
  fields,
  meta,
  badges,
  compactHeadWrap,
  decisionSlot,
  fieldsInline,
  tight = false
}: IRowBodyProps) => {
  const isCompact = variant === 'compact';

  return (
    <Stack
      data-testid='list-row-body'
      sx={tight ? TIGHT_BODY_SX : { flex: 1, minWidth: 0, gap: 1 }}
    >
      <RowHeader
        variant={variant}
        title={title}
        titleActions={titleActions}
        meta={meta}
        compactHeadWrap={compactHeadWrap}
        badges={isCompact ? badges : undefined}
      />

      {/* `compact` кладёт `badges` в саму голову, флекс-соседом title/meta
          (см. `RowHeader`, 18976, Р56) — отдельный ряд здесь остался бы
          вторым, пустым по смыслу дублем той же плашки */}
      {!isCompact && buildListBadgesRow(badges)}

      {!!subtitle && (
        <Typography component='div' sx={getSubtitleSx(subtitleMuted, tight)}>
          {subtitle}
        </Typography>
      )}

      {!isCompact && buildFieldsRow(fields, decisionSlot, fieldsInline, tight)}
    </Stack>
  );
};

interface IRowShellProps {
  variant: TListRowVariant;
  trailingAlign: TListRowTrailingAlign;
  surface: boolean;
  isInteractive: boolean;
  trailing?: ReactNode;
  footer?: ReactNode;
  decisionCorner?: ReactNode;
  children: ReactNode;
}

/**
 * Внешняя обёртка строки — единственный носитель контейнмента и поверхности
 * (`ROW_CONTAINER_SX`, см. её JSDoc), а заодно якорь обоих абсолютных углов.
 * Вынесена из `ListRowFrame` отдельным компонентом: четыре независимых ветки
 * («рисуем ли поверхность», футер, верхний угол, нижний угол) складывались со
 * ветвлением самого каркаса в цикломатическую сложность выше порога линтера.
 * Дерево рендерится то же, что было инлайновым, — узлы и `data-testid` не
 * менялись.
 * @param props - пропсы
 * @param props.variant - `list` или `compact` (инсет верхнего угла растёт
 * только у `list`, см. `getCornerSx`)
 * @param props.trailingAlign - угол `trailing` у верхней кромки или по центру строки
 * @param props.surface - рисовать ли поверхность строки (см. `IListRowFrameProps`)
 * @param props.isInteractive - ведёт ли клик по строке куда-то (18976, Р67)
 * @param props.trailing - группа угловых действий: верхний правый угол
 * @param props.footer - нижняя полоса ПОД содержимым, вне абсолютных углов
 * @param props.decisionCorner - пара решения `compact`: нижний правый угол
 * (в `list` она лежит в потоке, в ряду полей, и сюда не приходит)
 * @param props.children - содержимое строки (медиа + текстовый блок)
 */
const RowShell = ({
  variant,
  trailingAlign,
  surface,
  isInteractive,
  trailing,
  footer,
  decisionCorner,
  children
}: IRowShellProps) => {
  const theme = useTheme();

  return (
    // Порядок и хвостовой `...passedSx({})` — тот же приём, что уже несут
    // `CardListRow`/`CastingListRow` (bare `getRowSurfaceSx` + спред
    // `passedSx(...)` в конце массива `sx`): без него TS не принимает
    // `getRowSurfaceSx` элементом массива (его возвращаемый тип шире, чем
    // ждёт `SxProps`, конфликт возникает только вне этого паттерна).
    <Box
      sx={[
        ...(surface
          ? [
              (surfaceTheme: Theme) =>
                getRowSurfaceSx(surfaceTheme, isInteractive)
            ]
          : []),
        ROW_CONTAINER_SX,
        ...passedSx({})
      ]}
    >
      {/* `footer` — простой блок ПОД содержимым (18976, Р73, четвёртый заход:
          двухколоночный грид убран — пара решения больше не приходит сюда,
          а `footer` остался только подписью недоступности, ей колонка не
          нужна) */}
      {children}
      {!!footer && <Box data-testid='list-row-footer'>{footer}</Box>}

      {/* Абсолютный угол — сосед содержимого/футера, а не их часть (18976,
          Р73): расстояние до края читается от `ROW_CONTAINER_SX` целиком */}
      {!!trailing && (
        <Box
          data-testid='list-row-trailing'
          sx={getCornerSx(theme, variant, trailingAlign)}
        >
          {trailing}
        </Box>
      )}

      {/* Нижний угол `compact` — зеркало верхнего, см. `getDecisionCornerSx` */}
      {!!decisionCorner && (
        <Box data-testid='list-row-decision' sx={getDecisionCornerSx(theme)}>
          {decisionCorner}
        </Box>
      )}
    </Box>
  );
};

/**
 * Куда уходит `decisionSlot`: в `compact` — в АБСОЛЮТНЫЙ нижний угол
 * (`getDecisionCornerSx`), в `list` — в поток `RowBody`, последним элементом
 * ряда полей (`LIST_FIELDS_SX`, см. `DECISION_SLOT_SX`). Разводится отдельной
 * функцией, а не двумя тернарниками в теле каркаса: правило одно, а записанное
 * дважды оно и читается как два независимых, и считается линтером за две ветки.
 * @param isCompact - компактный ли вид строки
 * @param decisionSlot - пара решения, если домен её передал
 */
const splitDecisionSlot = (
  isCompact: boolean,
  decisionSlot?: ReactNode
): { compactDecision?: ReactNode; listDecision?: ReactNode } =>
  isCompact
    ? { compactDecision: decisionSlot }
    : { listDecision: decisionSlot };

export interface IListRowFrameProps {
  variant: TListRowVariant;
  media?: ReactNode;
  mediaAspect?: TListRowMediaAspect;
  /** Узкое фото человека 56×75 вместо 84×112 (только `3/4`; опт-ин, 19633) */
  mediaNarrow?: boolean;
  mediaAlign?: TListRowMediaAlign;
  title: ReactNode;
  titleActions?: ReactNode;
  subtitle?: ReactNode;
  subtitleMuted?: boolean;
  fields?: IRowField[];
  meta?: ReactNode;
  badges?: ReactNode;
  compactHeadWrap?: TCompactHeadWrap;
  trailing?: ReactNode;
  footer?: ReactNode;
  decisionSlot?: ReactNode;
  contentWrapper?: (content: ReactNode) => ReactNode;
  sx?: SxProps<Theme>;
  surface?: boolean;
  isInteractive?: boolean;
  /**
   * Где угол `trailing` по вертикали: `top` (дефолт — общее правило 18976, Р73) или
   * `center` (проект `compact`, макет v2 §4). При `center` резерв под угол домен
   * держит паддингом корня (`sx`), а не `titleActions`: тот резервирует только
   * первую строку головы
   */
  trailingAlign?: TListRowTrailingAlign;
  /** Поля `list` парой «подпись значение» в строку и ниже `WIDE_ROW` (проект, макет v2 §2) */
  fieldsInline?: boolean;
  tight?: boolean;
}

/**
 * Общий каркас строки списка — единая геометрия для актёра, визитки, кастинга и
 * проекта (18976). Домен решает СОДЕРЖИМОЕ (что за медиа, какие три поля, что в
 * бейджах), каркас — ГЕОМЕТРИЮ (высоты, паддинги, типографику полей, единый
 * порог контейнера). `list` держит ровно три подписанных поля колонкой,
 * `compact` — одну строку через «·», которую строит домен и отдаёт целиком в
 * `meta` (домен может собрать плотную шапку сам внутри `title` — так делают
 * проекты; типографика «·»-разделителя — забота домена: у каждого свой набор
 * термов и свои ограничения ширины, см. `modules/visual-hierarchy.md`).
 *
 * Строка сама себе контейнер (`container-type: inline-size`): она живёт и в
 * колонке рядом с рельсой фильтров, и во всю ширину десктопа, а ширину окна для
 * этого знать не нужно и нельзя.
 * @param props - пропсы
 * @param props.variant - `list` (три поля) или `compact` (одна строка)
 * @param props.media - содержимое медиа-слота; без него слот не рисуется вовсе
 * @param props.mediaAspect - пропорция медиа: `3/4` (фото человека, по умолчанию),
 * `2/3` (постер проекта, 56×84) или `1/1` (логотип, 56×56)
 * @param props.mediaNarrow - `3/4` узким 56×75 (ширина как у постера и логотипа); без флага 84×112
 * @param props.mediaAlign - выключка медиа по кросс-оси строки: `center`
 * (дефолт, портретное медиа) или `flex-start` (обложка проекта — см.
 * `TListRowMediaAlign`); в `list` ей следует и текстовый блок (см. `getRootSx`)
 * @param props.title - заголовок строки (имя, ФИО, название)
 * @param props.titleActions - действия строки заголовка (сердце[+папка]);
 * флекс-сосед именно заголовка, а не всей текстовой колонки (18976, Р3, см.
 * `RowBody`)
 * @param props.subtitle - вторая строка (доп. информация, название проекта у
 * кастинга, приписка вырожденного вида)
 * @param props.subtitleMuted - вторая строка приглушена (`text.disabled`) —
 * для приписки вырожденного вида, объясняющей пустое состояние, а не данные
 * @param props.fields - РОВНО три поля `list`; в `compact` не используется
 * @param props.meta - готовая строка метаданных `compact`, собранная доменом
 * @param props.badges - статусные чипы/бейджи
 * @param props.compactHeadWrap - порог головы `compact`: уже него `meta` второй
 * строкой, пара решения — своей полосой внизу (18976, Р8, см. `RowBody`)
 * @param props.trailing - группа угловых действий строки (кебаб, звезда,
 * папка, статусные иконки). ФИКСИРОВАННЫЙ верхний правый угол строки в ОБОИХ
 * видах (18976, Р73, четвёртый заход) — `position: absolute` от
 * `ROW_CONTAINER_SX`, независимо от того, есть ли у строки `footer`/
 * `decisionSlot`. Группа не занимает поток — домен ОБЯЗАН зарезервировать под
 * неё место в `titleActions`, иначе заголовок наедет на неё текстом.
 * @param props.footer - нижняя полоса под строкой (подпись недоступности:
 * «Кастинг завершён»/«удалён») — вне текстового блока и вне трёх полей, но
 * ВНУТРИ контейнерного контекста каркаса (см. `ROW_CONTAINER_SX`), блочный
 * элемент ПОД содержимым на любой ширине. Пара решения («Отклонить»/
 * «Принять») сюда больше НЕ приходит (18976, Р73, четвёртый заход — заказчик
 * прямо потребовал убрать её из отдельной полосы под строкой) — для нее см.
 * `props.decisionSlot`. `footer` не получает `opacity`, которым каркас гасит
 * недоступную строку (`props.sx`): это отдельный узел рядом с содержимым, а
 * не его потомок.
 * @param props.decisionSlot - пара решения («Отклонить»/«Принять», 18976,
 * Р73, четвёртый заход) — правый НИЖНИЙ угол строки, на одной линии с рядом
 * подписанных полей `list` (`LIST_FIELDS_SX`) или отдельной строкой под
 * шапкой `compact`; см. JSDoc `DECISION_SLOT_SX`/`RowBody`. В отличие от
 * `trailing`, это НЕ абсолютный оверлей — узел лежит в потоке `RowBody`,
 * поэтому получает `opacity` строки наравне с остальным содержимым и НЕ
 * требует резерва в `titleActions`.
 * @param props.contentWrapper - оборачивает СОДЕРЖИМОЕ строки (медиа/тело/
 * трейлинг/`decisionSlot`), но не `footer`. Нужен домену, который делает
 * строку целиком ссылкой (`CardListRow`: `<Link>` вокруг медиа+имени+полей),
 * но держит `footer` СНАРУЖИ этой ссылки. Кнопки решения (`decisionSlot`)
 * остаются ВНУТРИ обёрнутого содержимого — домен, чьи кнопки не должны
 * попасть в `<a>`, продолжает гасить клик по ним самостоятельно
 * (`preventDefault`/`stopPropagation`, см. `CastingResponseStatusBlock`), тем
 * же приёмом, что уже несут кнопки решения `ActorCastingCard`/
 * `ResponseCastingCard` — они и раньше жили внутри ссылки строки.
 * @param props.sx - стили корня строки
 * @param props.surface - рисовать ли поверхность строки (фон/рамка/радиус/hover).
 * По умолчанию `true`; рисуется на обёртке каркаса (`ROW_CONTAINER_SX`), не на
 * корне (`getRootSx`) — обёртка теперь единственный узел БЕЗ самоссылки на
 * контейнмент, поэтому и поверхность, и контейнмент внешние. Домен, которому
 * нужна ОДНА поверхность на строку И на содержимое, живущее вне каркаса
 * (нижняя полоса решения у кастинга, абсолютный оверлей сердца у актёра),
 * выключает её здесь и рисует сам — тем же `getRowSurfaceSx`, но на СВОЁМ
 * внешнем контейнере, который охватывает каркас и этот внекаркасный контент
 * одной рамкой.
 * @param props.isInteractive - ведёт ли клик по строке куда-то (18976, Р67).
 * По умолчанию `true`; влияет только на ховер-тень поверхности (`surface`) —
 * строка без клика (реестр визиток) не должна её обещать.
 * @param props.trailingAlign - угол `trailing` по вертикали: `top` (дефолт) или `center`
 * @param props.fieldsInline - поля `list` парой в строку и ниже `WIDE_ROW` (проект)
 * @param props.tight - плотный вертикальный ритм (карточки уведомлений, 19633): название 1.3, мета +2px 1.45,
 * поля +6px 1.6 без зазоров; по умолчанию выкл — списки не меняются
 */
export const ListRowFrame = ({
  variant,
  media,
  mediaAspect = '3/4',
  mediaNarrow,
  mediaAlign = 'center',
  title,
  titleActions,
  subtitle,
  subtitleMuted,
  fields,
  meta,
  badges,
  compactHeadWrap,
  trailing,
  footer,
  contentWrapper,
  sx,
  surface = true,
  isInteractive = true,
  decisionSlot,
  trailingAlign = 'top',
  fieldsInline = false,
  tight
}: IListRowFrameProps) => {
  const isCompact = variant === 'compact';
  // `compact` держит decisionSlot АБСОЛЮТНЫМ нижним углом (см.
  // `getDecisionCornerSx`), а не в потоке `RowBody` — только `list` кладёт его
  // в ряд полей (`LIST_FIELDS_SX`, см. `DECISION_SLOT_SX`)
  const { compactDecision, listDecision } = splitDecisionSlot(
    isCompact,
    decisionSlot
  );

  // Медиа-слот и корень читают одни параметры: от пропорции и выключки медиа
  // зависят и планка высоты, и выключка текста (см. `getRootSx`)
  const slot = {
    variant,
    aspect: mediaAspect,
    align: mediaAlign,
    narrow: mediaNarrow,
    media
  };
  const rowBox = (
    <Box
      sx={[
        getRootSx(variant, !!compactDecision, slot),
        getDecisionLaneSx(!!compactDecision, compactHeadWrap),
        ...passedSx(sx ?? {})
      ]}
    >
      <RowMedia {...slot} />

      <RowBody
        variant={variant}
        title={title}
        titleActions={titleActions}
        subtitle={subtitle}
        subtitleMuted={subtitleMuted}
        fields={fields}
        meta={meta}
        badges={badges}
        compactHeadWrap={compactHeadWrap}
        decisionSlot={listDecision}
        fieldsInline={fieldsInline}
        tight={tight}
      />
    </Box>
  );

  const content = contentWrapper ? contentWrapper(rowBox) : rowBox;

  return (
    <RowShell
      variant={variant}
      trailingAlign={trailingAlign}
      surface={surface}
      isInteractive={isInteractive}
      trailing={trailing}
      footer={footer}
      decisionCorner={compactDecision}
    >
      {content}
    </RowShell>
  );
};
